import {addToast, Button, Chip, Icon, IconButton, SplitButton, Text} from '@albert-io/atomic';
import {useDraggable} from '@dnd-kit/core';
import cx from 'classnames';
import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';

import {ItemBankRefsContext} from '../FitbItemBank/components/ItemBankRefsContext';
import {supplementRegexBaseString} from 'client/generic/MarkdownRendererV2/rules/supplementRules';
import supplementStore from 'client/Supplement/SupplementStore';
import supplementActions from 'client/Supplement/SupplementActions';
import {callAction} from 'client/framework';
import {renderStringOrSupplement} from './utils';
import {extractSupplementParts} from './utils';
import {getDraggableClasses} from './DndContextWrapper';
import Passage from 'client/SupplementTypes/Passage/Passage';
import {ImageFullScreenModal} from 'client/SupplementTypes/Image/ImageFullScreenModal.react';
import {getCroppedImageUrl} from 'client/SupplementTypes/Image/utils';
import constants from 'client/constants';
import {normalizeError} from 'lib/normalizeError';
import classNames from 'classnames';
import {useIsFeatureFlagEnabled} from 'client/components/FeatureFlag/featureFlag.utils';

export type DraggableChipSize = 'default' | 'small';

export const SMALL_SUPPLEMENTS_FLAG = 'small_supplements';

export const useSmallSupplementsFlag = () => {
  const {isFeatureEnabled, isLoading} = useIsFeatureFlagEnabled(SMALL_SUPPLEMENTS_FLAG);
  return {isEnabled: isFeatureEnabled, isLoading};
};

export const useIsSmallSupplementsEnabled = () => useSmallSupplementsFlag().isEnabled;

const SUPPLEMENT_TOKEN_REGEX = new RegExp(supplementRegexBaseString);

type SmallSupplementType = 'image' | 'audio' | 'passage';

export const getSmallSupplement = (value: string) => {
  const match = SUPPLEMENT_TOKEN_REGEX.exec(value);
  if (!match) return null;
  const id = match[1];
  if (!supplementStore.hasSupplement(id)) return null;
  const supplement = supplementStore.getSupplement(id);
  const type = supplement.getType();
  if (type === 'image' || type === 'audio' || type === 'passage') return {type, supplement};
  return null;
};

/**
 * Sizing for a small-supplement chip wrapper inside a flex-wrap row (item bank, ordering answer,
 * correct-order display). A fixed-width box that neither grows nor shrinks, so removing a sibling
 * doesn't make the remaining supplements stretch to fill the freed width — they keep their size.
 */
export const SMALL_SUPPLEMENT_WRAPPER_CLASS = 'grow-0 shrink-0 basis-[200px] max-w-full min-w-x15';

/**
 * Ensures the supplement referenced by a token in `value` is fetched into the store and reports
 * when it's ready. Mirrors SupplementRenderer's fetch-on-demand so a small-supplement chip can
 * render the compact view from the first paint instead of briefly flashing the full-size
 * supplement while the supplement loads. No-ops (always ready) when `enabled` is false.
 */
export const useEnsureSupplementLoaded = (value: string, enabled: boolean) => {
  const match = enabled ? SUPPLEMENT_TOKEN_REGEX.exec(value) : null;
  const id = match ? match[1] : null;
  const [isReady, setIsReady] = useState(() => (id ? supplementStore.hasSupplement(id) : true));

  useEffect(() => {
    if (!id || supplementStore.hasSupplement(id)) {
      // oxlint-disable-next-line react/react-compiler -- one-time supplement fetch/readiness check against the external supplement store
      setIsReady(true);
      return;
    }
    let cancelled = false;
    setIsReady(false);
    callAction(supplementActions.FETCH_SUPPLEMENT, id);
    Promise.resolve(supplementStore.getFetchSupplementPromise())
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setIsReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  return isReady;
};

const SmallAudioSupplement = ({
  src,
  caption,
  isOptionEliminated
}: {
  src: string;
  caption: string;
  isOptionEliminated?: boolean;
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    if (isOptionEliminated) {
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        setIsPlaying(false);
      }
    }
  }, [isOptionEliminated]);

  const togglePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation();
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio
        .play()
        .then(() => setIsPlaying(true))
        .catch((err) => {
          const error = normalizeError(err);
          addToast({
            color: 'negative',
            title: 'Error',
            message: error.message
          });
          setIsPlaying(false);
        });
    }
  };

  return (
    <div className='gap-x1 p-x2 relative flex w-full min-w-fit flex-col items-center text-center'>
      <div>
        <Icon icon='file-audio' iconStyle='light' className='size-x4' />
        <audio ref={audioRef} src={src} onEnded={() => setIsPlaying(false)}>
          <track kind='captions' />
        </audio>
      </div>
      <IconButton
        color={isPlaying ? 'primary' : 'secondary'}
        variant={isPlaying ? 'solid' : 'outlined'}
        className={classNames(
          'audio-player__button absolute top-x1 right-x1 size-x4! shrink-0 cursor-pointer! shadow-l1 **:cursor-pointer!',
          {
            'border border-border-primary': !isPlaying
          }
        )}
        onClick={togglePlayPause}
        icon={isPlaying ? 'pause' : 'play'}
        aria-hidden={true}
        size='s'
      />
      {caption && (
        <Text className='audio-player__title text-s line-clamp-2 min-w-full text-wrap wrap-break-word'>
          {caption}
        </Text>
      )}
    </div>
  );
};

const SmallImageSupplement = ({
  supplement
}: {
  supplement: ReturnType<typeof supplementStore.getSupplement>;
}) => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const cropValues = supplement.getCropValues?.();
  const hasCrop = cropValues && !cropValues.isEmpty?.();

  const [src, setSrc] = useState<string | null>(() => {
    if (!supplement.getImageLocation?.()?.path) return null;
    if (!hasCrop) return supplement.getImageLocation().href;
    return null;
  });

  useEffect(() => {
    if (src) return;
    if (!supplement.getImageLocation?.()?.path) return;
    if (hasCrop) {
      getCroppedImageUrl(supplement).then(setSrc);
    }
  }, [supplement, src, hasCrop]);

  if (!src) return null;
  return (
    <div className='relative flex max-h-[168px] w-full justify-center overflow-hidden'>
      <img
        src={src}
        alt={supplement.getAlttext?.() || ''}
        className='block min-h-0 max-w-full min-w-0 rounded object-contain'
        draggable={false}
      />
      <IconButton
        color='secondary'
        variant='outlined'
        icon={['far', 'arrow-up-right-and-arrow-down-left-from-center']}
        size='s'
        label='Fullscreen'
        className='top-x1 right-x1 border-border-primary shadow-l1 absolute cursor-pointer! border **:cursor-pointer!'
        onClick={(e) => {
          e.stopPropagation();
          setIsFullScreen(true);
        }}
      />
      {isFullScreen && (
        <ImageFullScreenModal
          isMobile={false}
          caption={supplement.getCaption?.() || ''}
          setIsFullScreen={setIsFullScreen}
          supplement={supplement}
        />
      )}
    </div>
  );
};

export const SmallSupplementContent = ({
  type,
  supplement,
  isOptionEliminated = false,
  className
}: {
  type: SmallSupplementType;
  supplement: ReturnType<typeof supplementStore.getSupplement>;
  isOptionEliminated?: boolean;
  className?: string;
}) => {
  if (type === 'image') return <SmallImageSupplement supplement={supplement} />;

  if (type === 'audio') {
    const audioFile = supplement.getAudio?.();
    if (!audioFile) return null;
    const src = `${constants.SUPPLEMENTS_ORIGIN}/${audioFile}`;
    const caption = supplement.getCaption?.() || supplement.getName?.() || '';
    return (
      <SmallAudioSupplement src={src} caption={caption} isOptionEliminated={isOptionEliminated} />
    );
  }

  // passage
  const content = supplement.getContent?.();
  if (!content) return null;
  return (
    <div className='w-full overflow-hidden'>
      <Passage
        content={content}
        showBorders={false}
        showHeader={false}
        forceMaxHeight
        isSmall
        className={className}
      />
    </div>
  );
};

/**
 * Renders a value that may contain a supplement token, choosing the compact supplement view when
 * the small-supplements feature is enabled and the token resolves to an image/audio/passage.
 * Ensures the referenced supplement is loaded first, so the compact view shows from the first paint
 * instead of briefly flashing the full-size supplement. Use this for raw supplement content that is
 * NOT wrapped in a draggable chip (e.g. correct-answer displays, slot labels).
 */
export const SupplementContent = ({
  value,
  enabled = true,
  className
}: {
  value: string;
  enabled?: boolean;
  className?: string;
}) => {
  const isReady = useEnsureSupplementLoaded(value, enabled);
  const smallSupplement = enabled ? getSmallSupplement(value) : null;

  if (enabled && !isReady && SUPPLEMENT_TOKEN_REGEX.test(value)) {
    return null;
  }

  if (smallSupplement) {
    return (
      <SmallSupplementContent
        type={smallSupplement.type}
        supplement={smallSupplement.supplement}
        className={className}
      />
    );
  }

  return <>{renderStringOrSupplement(value)}</>;
};

/**
 * Pure visual chip that renders a string or supplement token.
 * Use this inside DragOverlay (renderOverlay)
 *
 * When size='small' and the value is an image/audio/passage token, uses SmallSupplementContent
 */
interface SupplementChipProps {
  value: string;
  size?: DraggableChipSize;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
  onRemove?: (e: React.MouseEvent) => void;
  isOptionEliminated?: boolean;
  /** When true, suppresses interactive role on the inner element (use when already wrapped by a draggable parent). */
  nested?: boolean;
}

export const SupplementChip = ({
  value,
  size = 'default',
  className,
  onClick,
  onRemove,
  isOptionEliminated,
  nested
}: SupplementChipProps) => {
  const {isEnabled: isSmallSupplementsEnabled, isLoading: isFlagLoading} =
    useSmallSupplementsFlag();
  const isSmallChip = size === 'small';
  const isSupplementReady = useEnsureSupplementLoaded(
    value,
    isSmallChip && isSmallSupplementsEnabled
  );
  const smallSupplement =
    isSmallSupplementsEnabled && isSmallChip ? getSmallSupplement(value) : null;

  // For a small-size supplement chip, defer rendering until the flag has resolved AND the
  // referenced supplement is loaded — otherwise we'd briefly flash the full-size supplement
  // (rendered via the regular fallback) before switching to the compact view.
  if (
    isSmallChip &&
    SUPPLEMENT_TOKEN_REGEX.test(value) &&
    (isFlagLoading || (isSmallSupplementsEnabled && !isSupplementReady))
  ) {
    return null;
  }

  const parts = smallSupplement ? extractSupplementParts(value) : null;
  const surroundingText = parts?.text || '';

  const badgeContent = smallSupplement ? (
    <>
      {surroundingText && <span className='mr-x0-half'>{surroundingText}</span>}
      <SmallSupplementContent
        type={smallSupplement.type}
        supplement={smallSupplement.supplement}
        isOptionEliminated={isOptionEliminated}
      />
    </>
  ) : (
    renderStringOrSupplement(value)
  );

  if (onRemove && smallSupplement) {
    return (
      <div
        {...(!nested && {
          role: 'button' as const,
          tabIndex: 0
        })}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (!nested && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onClick?.(e as any);
          }
        }}
        onClick={onClick}
        className={cx(
          'min-w-0 p-0 relative flex overflow-hidden rounded-lg border border-border-primary background-primary text-left font-normal',
          onClick ? 'cursor-grab' : 'cursor-default',
          className
        )}
      >
        {badgeContent}
        <IconButton
          color='secondary'
          variant='outlined'
          icon='xmark'
          size='s'
          label='Remove'
          className='top-x1 left-x1 border-border-primary shadow-l1 absolute cursor-pointer! border **:cursor-pointer!'
          onClick={(e) => {
            e.stopPropagation();
            onRemove(e);
          }}
        />
      </div>
    );
  }

  if (onRemove) {
    return (
      <SplitButton
        className={cx(
          'm-x0! max-w-full',
          '[&:hover>.a-button]:[--button-border-color:transparent]',
          '[&:hover>.a-button]:[--button-border-color--hover:transparent]',
          '[&:hover>.a-button]:[--button-border-color--active:transparent]',
          '[&:hover>.a-button:first-child]:[border-right:1px_solid_var(--color-border-primary)]',
          className
        )}
      >
        <Button
          variant='outlined'
          color='secondary'
          onClick={onClick}
          className='hover:background-transparent! min-w-0 cursor-grab overflow-hidden'
          size='s'
        >
          <span className='line-clamp-1 min-w-0'>{badgeContent}</span>
        </Button>
        <Button
          variant='outlined'
          color='secondary'
          onClick={(e) => {
            e.stopPropagation();
            onRemove(e);
          }}
          aria-label='Remove'
          className='hover:background-transparent! cursor-pointer! **:cursor-pointer!'
          size='s'
        >
          <Icon icon='xmark' />
        </Button>
      </SplitButton>
    );
  }

  // For small supplements, use a plain container to keep chip and supplement styles independent
  if (smallSupplement) {
    return (
      <div
        {...(!nested && {
          role: 'button' as const,
          tabIndex: 0
        })}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (!nested && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onClick?.(e as any);
          }
        }}
        onClick={onClick}
        className={cx(
          'min-w-0 p-0 flex w-full overflow-hidden rounded-lg border border-border-primary background-primary text-left font-normal',
          onClick ? 'cursor-grab' : 'cursor-default',
          className
        )}
      >
        {badgeContent}
      </div>
    );
  }

  return (
    <Chip
      color='neutral'
      size='small'
      onClick={onClick}
      noMargin
      className={cx(
        'min-w-0 cursor-grab overflow-hidden border border-border-primary background-primary! object-contain',
        className
      )}
    >
      <span className='truncate'>{badgeContent}</span>
    </Chip>
  );
};

/**
 * Draggable chip wrapping dnd-kit's useDraggable and ItemBankRefsContext registration.
 * Use this as the actual draggable item in the question input area.
 *
 * @prop size - 'default' | 'small'. Controls chip padding/text size for compact contexts.
 */
interface DraggableChipProps {
  id: string;
  value: string;
  size?: DraggableChipSize;
  isActive?: boolean;
  isDragging?: boolean;
  activeItemId?: string | null;
  onClick?: (e: React.MouseEvent) => void;
  onRemove?: (e: React.MouseEvent) => void;
  /** When true, the chip never dims because another item is being dragged/selected. Used for items already placed in an answer. */
  suppressDimming?: boolean;
}

export const DraggableChip = ({
  id,
  value,
  size = 'default',
  isActive,
  isDragging,
  activeItemId,
  onClick,
  onRemove,
  suppressDimming
}: DraggableChipProps) => {
  const {attributes, listeners, setNodeRef, transform} = useDraggable({id});
  const {registerItemEl} = useContext(ItemBankRefsContext);

  const itemRef = useCallback(
    (el: HTMLDivElement | null) => {
      setNodeRef(el);
      registerItemEl(id, el);
    },
    [setNodeRef, registerItemEl, id]
  );

  const isDraggingThis = !!transform;
  const isAnotherItemActive = activeItemId !== null && !isActive;

  let shouldShowReducedOpacity = false;
  if (isDraggingThis || suppressDimming) {
    shouldShowReducedOpacity = false;
  } else if (isDragging || isAnotherItemActive) {
    shouldShowReducedOpacity = true;
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick?.(e);
  };

  const isSupplement = !!extractSupplementParts(value);
  let draggableClasses = getDraggableClasses(isDraggingThis);
  if (isSupplement) {
    draggableClasses = draggableClasses.replace('hover:background-subtle-hover!', '');
  }

  return (
    <div ref={itemRef} {...attributes} {...listeners} data-item-id={id}>
      <SupplementChip
        value={value}
        size={size}
        onClick={handleClick}
        onRemove={onRemove}
        nested
        className={cx(draggableClasses, {
          '-rotate-6 background-subtle-hover!': isActive,
          'opacity-20': isDraggingThis,
          'opacity-50': shouldShowReducedOpacity,
          'z-10 shadow-l1': isActive || isDragging
        })}
      />
    </div>
  );
};
